/**
 * Technology Stack Types
 * Type definitions for the tech stack database
 */

export interface TechnologyOption {
  id: string;
  name: string;
  category: string;
  description: string;
  website: string;

  // Licensing & Cost
  license: 'open-source' | 'commercial' | 'freemium' | 'enterprise-only';
  pricing: {
    model: 'free' | 'freemium' | 'usage-based' | 'per-seat' | 'enterprise' | 'hybrid';
    startingPrice?: string;
    freeOption: boolean;
  };

  // Technical Characteristics
  technical: {
    language: string[];
    architecture: string[];
    scalability: 'low' | 'medium' | 'high' | 'very-high';
    performance: 'low' | 'medium' | 'high' | 'very-high';
    learningCurve: 'easy' | 'moderate' | 'steep' | 'very-steep';
    maturity: 'experimental' | 'early' | 'stable' | 'mature' | 'legacy';
  };

  // Operational Requirements
  operations: {
    hostingOptions: ('self-hosted' | 'cloud' | 'hybrid' | 'managed')[];
    maintenanceComplexity: 'low' | 'medium' | 'high';
    monitoringComplexity: 'low' | 'medium' | 'high';
    backupRecovery: 'simple' | 'moderate' | 'complex';
  };

  // Ecosystem & Support
  ecosystem: {
    communitySize: 'small' | 'medium' | 'large' | 'very-large';
    documentation: 'poor' | 'fair' | 'good' | 'excellent';
    enterpriseSupport: boolean;
    integrations: string[];
    popularityTrend: 'declining' | 'stable' | 'growing' | 'rapidly-growing';
  };

  // Use Cases & Fit
  suitability: {
    teamSize: ('startup' | 'small' | 'medium' | 'large' | 'enterprise')[];
    useCases: string[];
    industries: string[];
    strengths: string[];
    weaknesses: string[];
    whenToUse: string[];
    whenNotToUse: string[];
  };

  // Compliance & Security
  compliance: {
    gdpr: boolean;
    hipaa: boolean;
    soc2: boolean;
    pci: boolean;
    security: {
      encryption: boolean;
      auditLogs: boolean;
      rbac: boolean;
      mfa: boolean;
    };
  };

  // Migration & Vendor Lock-in
  migration: {
    dataPortability: 'easy' | 'moderate' | 'difficult';
    vendorLockIn: 'none' | 'low' | 'medium' | 'high';
    migrationPath: string[];
  };

  // Related Content
  content: {
    comparisonSlug?: string;
    insightSlugs?: string[];
    frameworkSlugs?: string[];
  };
}

export const TECH_CATEGORIES = {
  DATABASE: 'database',
  CLOUD_PROVIDER: 'cloud-provider',
  CI_CD: 'ci-cd',
  MONITORING: 'monitoring',
  AUTH: 'authentication',
  API_GATEWAY: 'api-gateway',
  MESSAGE_QUEUE: 'message-queue',
  CACHE: 'cache',
  SEARCH: 'search',
  LOGGING: 'logging',
  CONTAINER_ORCHESTRATION: 'container-orchestration',
  FRONTEND_FRAMEWORK: 'frontend-framework',
  BACKEND_FRAMEWORK: 'backend-framework',
  MOBILE_FRAMEWORK: 'mobile-framework',
  TESTING: 'testing',
  OBSERVABILITY: 'observability',
  SECRET_MANAGEMENT: 'secret-management',
  CDN: 'cdn',
  EMAIL_SERVICE: 'email-service',
  ANALYTICS: 'analytics',
  AI_AGENTS: 'ai-agents',
  SERVICE_MESH: 'service-mesh',
  FEATURE_FLAGS: 'feature-flags',
  PAYMENTS: 'payments',
  HEADLESS_CMS: 'headless-cms',
  DATA_WAREHOUSE: 'data-warehouse',
  DATA_TRANSFORMATION: 'data-transformation',
  INFRASTRUCTURE_AS_CODE: 'infrastructure-as-code',
  CODE_QUALITY: 'code-quality',
  WORKFLOW_ORCHESTRATION: 'workflow-orchestration',
  META_FRAMEWORK: 'meta-framework',
  COMMUNICATIONS: 'communications',
  REAL_TIME: 'real-time',
  OBJECT_STORAGE: 'object-storage',
  MEDIA_PROCESSING: 'media-processing',
  ORM: 'orm',
  GRAPHQL: 'graphql',
  BUILD_TOOL: 'build-tool',
  MONOREPO: 'monorepo',
  STATE_MANAGEMENT: 'state-management',
  DESKTOP_FRAMEWORK: 'desktop-framework',
  STATIC_SITE_GENERATOR: 'static-site-generator',
  DOCUMENTATION_PLATFORM: 'documentation-platform',
  BACKGROUND_JOBS: 'background-jobs',
  ERROR_TRACKING: 'error-tracking',
  UI_LIBRARY: 'ui-library',
} as const;

export type TechCategory = (typeof TECH_CATEGORIES)[keyof typeof TECH_CATEGORIES];

export interface TechCategoryInfo {
  id: string;
  display_name: string;
  sort_order: number;
  technology_count: number;
}

export interface TechCategoryGroup {
  id: string;
  name: string;
  icon: string;
  sort_order: number;
  categories: string[];
}

export interface TechnologiesResponse {
  technologies: TechnologyOption[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface CategoriesResponse {
  groups: TechCategoryGroup[];
  categories: TechCategoryInfo[];
}
